<template>
  <div class="board">
    <Container
      class="board-content"
      orientation="horizontal"
      :drag-handle-selector="columnsDraggable ? '.column-drag-handle' : '.NOOP'"
      :drop-placeholder="upperDropPlaceholderOptions"
      @drop="onColumnDrop($event)"
      @drag-start="dragStart"
    >
      <Draggable v-for="column in columns" :key="column.value">
        <div class="column card-container">
          <div class="card-column-header">
            <span
              class="column-drag-handle"
              :class="{ hidden: !columnsDraggable }"
              >&#x2630;</span
            >
            {{ column.label }}
          </div>
          <Container
            ref="columns"
            class="column-content"
            group-name="col"
            :get-child-payload="getCardPayload(column.value)"
            drag-class="card-ghost"
            drop-class="card-ghost-drop"
            :drop-placeholder="dropPlaceholderOptions"
            @drop="(e) => onCardDrop(column.value, e)"
            @drop-ready="({ element }) => onDropReady({ element, column })"
            @drag-start="(e) => log('drag start', e)"
            @drag-end="(e) => log('drag end', e)"
          >
            <Draggable v-for="card in getCards(column.value)" :key="card.id">
              <div class="card-wrapper">
                <slot :class="card.props.className" :card="card"> </slot>
              </div>
            </Draggable>
          </Container>
        </div>
      </Draggable>
    </Container>
  </div>
</template>

<script>
import { Container, Draggable } from 'vue-smooth-dnd'
import { applyDrag, generateItems } from './helpers'
import { orderBy } from 'lodash'

const columnNames = ['Todos', 'In progress', 'Done', 'Archived']

export default {
  name: 'Board',

  components: { Container, Draggable },

  props: {
    columns: { type: Array, default: () => [] },
    columnsDraggable: { type: Boolean, default: true },
    cards: { type: Array, default: () => [] },
    columnCardKey: { type: String, default: '' },
  },

  data() {
    return {
      upperDropPlaceholderOptions: {
        className: 'cards-drop-preview',
        animationDuration: '150',
        showOnTop: true,
      },
      dropPlaceholderOptions: {
        className: 'drop-preview',
        animationDuration: '150',
        showOnTop: true,
      },
    }
  },

  methods: {
    getCards(columnValue) {
      const cards = this.cards.filter(
        (card) => card.columnValue === columnValue,
      )
      const sortedCards = orderBy(
        cards,
        ['position', 'updated_at'],
        ['asc', 'desc'],
      )
      return sortedCards
    },
    onColumnDrop(dropResult) {
      // noop
    },

    onCardDrop(columnValue, dropResult) {
      if (dropResult.removedIndex !== null || dropResult.addedIndex !== null) {
        const { removedIndex, addedIndex, payload } = dropResult
        this.$emit('card-drop', {
          column: columnValue,
          removedIndex,
          addedIndex,
          card: payload,
          columnCards: this.getCards(columnValue),
        })
      }
    },

    onDropReady({ element, column }) {
      const columnIdx = this.columns.indexOf(column)
      const columnContentVm = this.$refs.columns[columnIdx]
      const columnContent = columnContentVm.$el
      const topOffset = columnContent.scrollTop
      const dropPreview = columnContent.querySelector(
        '.smooth-dnd-drop-preview-constant-class',
      )
      if (dropPreview) {
        const newTop = parseInt(dropPreview.style.top, 10) + topOffset
        dropPreview.style.top = newTop + 'px'
      }
    },

    getCardPayload(columnValue) {
      return (index) => {
        return this.getCards(columnValue)[index]
      }
    },

    dragStart() {
      console.log('drag started')
    },

    log(...params) {
      console.log(...params)
    },
  },
}
</script>

<style lang="scss">
$card-margin-vertical: 4px;
$card-margin-horizontal: 2px;
$card-drop-preview-margin-vertical: $card-margin-vertical + 1px;
$card-drop-preview-margin-horizontal: $card-margin-horizontal + 1px;

.board {
  display: flex;
  position: relative;
  height: calc(100vh - 200px);

  .board-content {
    position: absolute;
    height: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 50px;

    .column {
      margin: 0px 4px;
      width: 272px;
      height: 100%;
      overflow-y: auto;

      .column-content {
        height: calc(100% - 40px);
        overflow-y: auto;
      }

      .column-content::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        border-radius: 5px;
        background-color: #f5f5f5;
      }

      .column-content::-webkit-scrollbar {
        width: 12px;
        background-color: #f5f5f5;
      }

      .column-content::-webkit-scrollbar-thumb {
        border-radius: 5px;
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        // background-color: #D62929;
        background-color: #bababa;
      }

      .card {
        margin-top: $card-margin-vertical;
        margin-bottom: $card-margin-vertical;
        margin-left: $card-margin-horizontal;
        margin-right: $card-margin-horizontal;
        padding: 5px;
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
}

.drop-preview {
  background-color: rgba(150, 150, 200, 0.1);
  border: 1px dashed #abc;
  margin-top: $card-drop-preview-margin-vertical;
  margin-bottom: $card-drop-preview-margin-vertical;
  margin-left: $card-drop-preview-margin-horizontal;
  margin-right: $card-drop-preview-margin-horizontal;
}

.cards-drop-preview {
  background-color: rgba(150, 150, 200, 0.1);
  border: 1px dashed #abc;
  margin: 5px 45px 5px 5px;
}

.card-ghost {
  transition: transform 0.18s ease;
  transform: rotateZ(5deg);
  cursor: grabbing;
}

.opacity-ghost {
  transition: all 0.18s ease;
  opacity: 0.8;
  /* transform: rotateZ(5deg); */
  background-color: cornflowerblue;
  box-shadow: 3px 3px 10px 3px rgba(0, 0, 0, 0.3);
}

.opacity-ghost-drop {
  opacity: 1;
  /* transform: rotateZ(0deg); */
  background-color: white;
  box-shadow: 3px 3px 10px 3px rgba(0, 0, 0, 0);
}
</style>
